import useAIChat from '@/composables/ai-chat/ai-chat';
import useLocalStorage from '@/composables/local-storage/local-storage';
import useLoggedInUserAccount from '@/composables/logged-in-user-account/logged-in-user-account';
import { AI_CHAT } from '@/route-names';
import { DEFAULT_WIDTH_AI_CHAT_PANEL, EVENTS } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

const defaultState = () => ({ open: false, activeChatId: null, width: DEFAULT_WIDTH_AI_CHAT_PANEL });

const sharedState = new Map();

const stateForKey = (key) => {
  if (!sharedState.has(key)) {
    const { data } = useLocalStorage(localStorage, { getKey: () => ref(key) }, defaultState());
    sharedState.set(key, data);
  }
  return sharedState.get(key);
};

export const resetSharedState = () => { sharedState.clear(); };

export default function useAiChatPanel() {
  const route = useRoute();
  const store = useStore();
  const { loggedInUserAccount } = useLoggedInUserAccount();
  const { canUseAIChat } = useAIChat();
  const key = computed(() => `${loggedInUserAccount.value?.uid}_ai-chat_panel`);

  const state = () => stateForKey(key.value);
  const patch = (values) => {
    const data = state();
    data.value = { ...defaultState(), ...data.value, ...values };
  };

  const fullScreen = computed(() => route.name === AI_CHAT);
  const smAndDown = computed(() => store.state.breakpoint.smAndDown);

  const open = computed(() => canUseAIChat.value
    && !fullScreen.value
    && !smAndDown.value
    && state().value.open === true);
  const activeChatId = computed(() => {
    const { activeChatId: id } = state().value;
    return id === undefined ? null : id;
  });
  const width = computed(() => state().value.width ?? DEFAULT_WIDTH_AI_CHAT_PANEL);

  const trackOpened = (via) => EventBus.$emit(EVENTS.AI_CHAT.CHAT_OPENED, { via });

  const close = () => patch({ open: false });
  const openPanel = () => {
    patch({ open: true });
    trackOpened('bubble');
  };
  const showChat = (uid) => patch({ open: true, activeChatId: uid });
  const rememberChat = (uid) => patch({ activeChatId: uid });
  const openChat = (uid) => {
    showChat(uid);
    trackOpened('history');
  };
  const newChat = () => {
    patch({ open: true, activeChatId: null });
    trackOpened('new_chat');
  };
  const setWidth = (value) => patch({ width: value });

  const trackFullScreenOpened = () => trackOpened('full_screen');

  return {
    open,
    activeChatId,
    width,
    close,
    openChat,
    showChat,
    rememberChat,
    newChat,
    setWidth,
    trackFullScreenOpened,
    openPanel,
  };
}
